import * as React from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';

import {
    Col,
    Progress as ProgressBar,
    Row
} from 'reactstrap';

import {
    GetBorrowerFlowAccountingPkg
} from '../../Store/ImportFinancials';

import { ApplicationState } from '../../Store';
import { pathConstants } from '../../Utils/Constants';

import { BorrowerPortalChildProps } from './BorrowerPortalV2';

import { PortalTextBorrowerProgress } from '../../Models/Api/strongbox.financialportal';
import { GetPortalText } from '../../Store/PortalAssets';

import {
    faCheck,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type InjectedReduxState = {
    progressSteps: string[];
    progressStepImages: ((className?: string) => React.ReactElement)[];
    accountingPkg?: string;
}

type InjectedActionCreators = {
};

type Props = BorrowerPortalChildProps & InjectedActionCreators & InjectedReduxState;

const delayPerTick = 100;
const stepIncrementPerTick = 0.05;
const finalDelay = 4000;

export const ProgressComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        progressSteps,
        progressStepImages,
    } = props;

    const [complete, setComplete] = React.useState<boolean>(false);
    const [step, setStep] = React.useState<number>(0.0);

    const progressComplete = (): void => {
        // There shouldn't really be a case where lineItems is null but we add the check here anyway.
        // The primary thing we are checking on is where we have made all the line item images visible and should go 
        // to a complete state.
        if (step + stepIncrementPerTick > progressSteps.length) {
            setComplete(true);
        } else {
            setStep(step + stepIncrementPerTick);
        }
    }

    React.useEffect(() => {
        let timeout = delayPerTick;

        if (step + stepIncrementPerTick > progressSteps.length) {
            timeout = finalDelay;
        }
        // On the last step, the set the delay to the final delay value, otherwise, just the normal delay between steps.

        const idTimeout = setTimeout(progressComplete, timeout);
        return (() => clearTimeout(idTimeout));

        // progressText and progressComplete won't actually change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const pctComplete = React.useMemo(() => {
        return (step / progressSteps.length) * 100;

    }, [progressSteps, step])

    if (complete) {
        return <Navigate to={pathConstants.linked} />
    }

    return (
        <>
            <Row style={{ width: '100%' }}>
                <Col xs={12}>
                    <ProgressBar
                        striped
                        value={pctComplete}
                        barClassName={'borrower-progress'}
                    />
                </Col>
            </Row>
            <Row style={{ marginTop: '25px' }}>
                <Col xs={12} className={'centered-col'}>
                    <table className={'line-items'}>
                        <tbody>
                            {progressSteps.map((lineItem, itemIndex) => {
                                const visibleClass = (itemIndex <= step) ? 'clear-image-fade-in-visible' : '';

                                return (
                                    <tr key={`progress-2934803294832048390-${itemIndex}`}>
                                        <td key="19483204832048" style={{
                                            width: '60px'
                                        }}>
                                            {progressStepImages[itemIndex](`clear-image-fade-in ${visibleClass}`)}
                                        </td>
                                        <td key="58924934923759">
                                            <div>
                                                <span style={{ marginBottom: '0px', alignSelf: 'left' }}>{lineItem}</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Col>
            </Row>
        </>
    );
}

export const Progress = connect<InjectedReduxState, InjectedActionCreators, BorrowerPortalChildProps, ApplicationState>(
    (appState: ApplicationState) => {
        const progressText = GetPortalText<PortalTextBorrowerProgress>(appState, 'borrowerProgress');
        const progressSteps: string[] = [
            progressText?.establishingSecureConnection || '',
            progressText?.connectionSuccessful || '',
            progressText?.gatheringData || '',
        ]

        const progressStepImages: ((className?: string) => React.ReactElement)[] = [
            (className?: string) => <FontAwesomeIcon className={className} icon={faCheck} />,
            (className?: string) => <FontAwesomeIcon className={className} icon={faCheck} />,
            (className?: string) => <FontAwesomeIcon className={className} icon={faCheck} />,
        ];

        const result = {
            progressSteps,
            progressStepImages,
            accountingPkg: GetBorrowerFlowAccountingPkg(appState),
        };

        return result;
    }
)(ProgressComponent);
